/**
 * Tenant Service
 * 
 * API calls for tenant management and multi-tenancy operations
 */

import apiClient from './apiClient';

const tenantService = {
  /**
   * Login with tenant selection support
   */
  loginWithTenants: async (username, password) => {
    const response = await apiClient.post('/tenant-auth/login', {
      username,
      password
    });
    return response.data;
  },

  /**
   * Select tenant after login
   */
  selectTenant: async (tenantId, temporaryToken) => {
    const response = await apiClient.post(
      '/tenant-auth/select-tenant',
      { tenantId },
      {
        headers: {
          Authorization: `Bearer ${temporaryToken}`
        }
      }
    );
    return response.data;
  },

  /**
   * Switch to a different tenant
   */
  switchTenant: async (tenantId) => {
    const response = await apiClient.post('/tenant-auth/switch-tenant', {
      tenantId
    });
    return response.data;
  },

  /**
   * Get user's tenant memberships
   */
  getUserMemberships: async () => {
    const response = await apiClient.get('/tenant-auth/memberships');
    return response.data;
  },

  /**
   * Get current tenant information
   */
  getCurrentTenant: async () => {
    const response = await apiClient.get('/tenant-auth/current-tenant');
    return response.data;
  },

  /**
   * Get all tenants (platform admin)
   */
  getAllTenants: async (params = {}) => {
    const response = await apiClient.get('/tenants', { params });
    return response.data;
  },

  /**
   * Get tenant by ID
   */
  getTenantById: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}`);
    return response.data;
  },

  /**
   * Create new tenant (platform admin)
   */
  createTenant: async (tenantData) => {
    const response = await apiClient.post('/tenants', tenantData);
    return response.data;
  },

  /**
   * Update tenant
   */
  updateTenant: async (tenantId, updates) => {
    const response = await apiClient.put(`/tenants/${tenantId}`, updates);
    return response.data;
  },

  /**
   * Delete tenant (platform admin)
   */
  deleteTenant: async (tenantId) => {
    const response = await apiClient.delete(`/tenants/${tenantId}`);
    return response.data;
  },

  /**
   * Get tenant configuration
   */
  getTenantConfiguration: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}/config`);
    return response.data;
  },

  /**
   * Update tenant configuration
   */
  updateTenantConfiguration: async (tenantId, config) => {
    const response = await apiClient.put(`/tenants/${tenantId}/config`, config);
    return response.data;
  },

  /**
   * Get tenant members
   */
  getTenantMembers: async (params = {}) => {
    const response = await apiClient.get('/tenant-memberships', { params });
    return response.data;
  },

  /**
   * Add member to tenant
   */
  addMember: async (userId, roleId) => {
    const response = await apiClient.post('/tenant-memberships', {
      userId,
      roleId
    });
    return response.data;
  },

  /**
   * Update member role
   */
  updateMemberRole: async (membershipId, roleId) => {
    const response = await apiClient.put(`/tenant-memberships/${membershipId}/role`, {
      roleId
    });
    return response.data;
  },

  /**
   * Remove member from tenant
   */
  removeMember: async (membershipId) => {
    const response = await apiClient.delete(`/tenant-memberships/${membershipId}`);
    return response.data;
  },

  /**
   * Invite user to tenant
   */
  inviteUser: async (email, roleId) => {
    const response = await apiClient.post('/tenant-memberships/invite', {
      email,
      roleId
    });
    return response.data;
  },

  /**
   * Accept invitation
   */
  acceptInvitation: async (invitationToken) => {
    const response = await apiClient.post('/tenant-memberships/accept', {
      invitationToken
    });
    return response.data;
  },

  /**
   * Get available subscription plans (public)
   */
  getAvailablePlans: async () => {
    const response = await apiClient.get('/tenant-onboarding/plans');
    return response.data;
  },

  /**
   * Register new tenant (public)
   */
  registerTenant: async (registrationData) => {
    const response = await apiClient.post('/tenant-onboarding/register', registrationData);
    return response.data;
  },

  /**
   * Verify email with token (public)
   */
  verifyEmail: async (token) => {
    const response = await apiClient.get(`/tenant-onboarding/verify-email/${token}`);
    return response.data;
  },

  /**
   * Validate slug availability
   */
  validateSlug: async (slug) => {
    const response = await apiClient.get(`/tenant-onboarding/validate-slug/${slug}`);
    return response.data;
  },

  /**
   * Validate subdomain availability
   */
  validateSubdomain: async (subdomain) => {
    const response = await apiClient.get(`/tenant-onboarding/validate-subdomain/${subdomain}`);
    return response.data;
  },

  /**
   * Suggest slug from academy name
   */
  suggestSlug: async (name) => {
    const response = await apiClient.get('/tenant-onboarding/suggest-slug', {
      params: { name }
    });
    return response.data;
  },

  /**
   * Request subscription change
   */
  requestSubscriptionChange: async (tenantId, requestData) => {
    const response = await apiClient.post(`/tenants/${tenantId}/subscription-request`, requestData);
    return response.data;
  },

  /**
   * Get deletion status
   */
  getDeletionStatus: async () => {
    const response = await apiClient.get('/tenant-data/deletion-status');
    return response.data;
  },

  /**
   * Export tenant data to Excel
   */
  exportTenantData: async () => {
    const response = await apiClient.post('/tenant-data/export', {}, {
      responseType: 'blob'
    });
    return response;
  },

  /**
   * Request data deletion (soft delete with 7-day grace period)
   */
  requestDataDeletion: async (confirmation) => {
    const response = await apiClient.post('/tenant-data/delete', { confirmation });
    return response.data;
  },

  /**
   * Restore deleted data (within 7 days)
   */
  restoreData: async () => {
    const response = await apiClient.post('/tenant-data/restore');
    return response.data;
  }
};

export default tenantService;
